import React, { useState } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import moment from 'moment';
import 'moment/locale/sv';
import { db } from '../firebaseConfig';
import { doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import calendarLine from '../images/calendar-line.svg';
import optionsIcon from './../images/adjust-line.svg';
import bookCover from '../images/empty-book.svg';
import BookDetails from './BookDetails';
import CurrentlyReading from './CurrentlyReading';
import { readingStatus, updateProtocol } from '../utils/functions';
import EmptyState from './EmptyState';
import { useTranslation } from '../i18n';
import BookInfo from './BookInfo';
import TextInput from './TextInput';
import StaticStarRating from './StaticStarRating';

const FilterInput = ({ searchText, setSearchText, t }) => (
  <div className="form filter-wrapper">
    <TextInput
      type="text"
      labelName={t.filter}
      value={searchText}
      onChangeFunction={(e) => setSearchText(e.target.value)}
      icon={optionsIcon}
      clear={true}
    />
  </div>
);

const UserBooks = ({
  user,
  userData,
  books,
  fetchBooks,
  fetchUserData,
  showNotification,
  updateCurrentlyReading,
}) => {
  const lang = localStorage.getItem('language');
  const { t } = useTranslation();
  const [moving, setMoving] = useState(false);
  const [searchText, setSearchText] = useState('');

  async function markBookAsRead(book, read, reading) {
    setMoving(true);
    const userBookRef = doc(db, 'UserBooks', book.id);

    try {
      await runTransaction(db, async (transaction) => {
        transaction.update(userBookRef, {
          status: read ? 'read' : reading ? 'reading' : 'wantToRead',
          dateStarted: reading ? serverTimestamp() : '',
          dateFinished: read ? serverTimestamp() : '',
        });

        // Update currentlyReading if status reading and book has pages
        if (reading && !isNaN(book.number_of_pages)) {
          updateCurrentlyReading(book, user, true);
        }
      });
      fetchBooks();
      if (!reading) {
        fetchUserData(user.email, false, 'markBookAsRead, UserBooks.js');
      }
      showNotification(
        `${book.title} ${t.movedto} ${readingStatus(read ? 'read' : reading ? 'reading' : 'wantToRead')}!`,
      );
    } catch (error) {
      console.error('Error updating user stats: ', error);
    } finally {
      setMoving(false);
    }
  }

  const booksReading = books.filter((book) => book.status === 'reading').length;
  const booksWantToRead = books.filter(
    (book) => book.status === 'wantToRead',
  ).length;
  const booksRead = books.filter((book) => book.status === 'read').length;
  const booksDNF = books.filter((book) => book.status === 'dnf').length;

  const matchesSearchText = (book) => {
    const searchString = searchText.toLowerCase();

    // Handle author_name being a string or an array
    const authorMatches = Array.isArray(book.author_name)
      ? book.author_name.some(
          (author) =>
            typeof author === 'string' &&
            author.toLowerCase().includes(searchString),
        )
      : typeof book.author_name === 'string' &&
        book.author_name.toLowerCase().includes(searchString);

    const titleMatches =
      typeof book.title === 'string' &&
      book.title.toLowerCase().includes(searchString);

    const matchesSearchText =
      searchText === '' || authorMatches || titleMatches;

    return matchesSearchText;
  };

  const filteredBooksWantToRead = books
    .filter((book) => {
      const matchesStatus = book.status === 'wantToRead';
      const matchesFilter = matchesSearchText(book);
      return matchesStatus && matchesFilter;
    })
    .sort((a, b) => a.createdAt - b.createdAt);

  const filteredBooksRead = books
    .filter((book) => {
      const matchesStatus = book.status === 'read';
      const matchesFilter = matchesSearchText(book);
      return matchesStatus && matchesFilter;
    })
    .sort((a, b) => a.createdAt - b.createdAt);

  const filteredBooksDNF = books
    .filter((book) => {
      const matchesStatus = book.status === 'dnf';
      const matchesFilter = matchesSearchText(book);
      return matchesStatus && matchesFilter;
    })
    .sort((a, b) => a.createdAt - b.createdAt);

  return (
    <div className="section">
      <Tabs>
        <TabList className="tabs">
          <Tab className="tab fs-l">{t.reading}</Tab>
          <Tab className="tab fs-l">{t.wanttoread}</Tab>
          <Tab className="tab fs-l">{t.haveread}</Tab>
          {booksDNF > 0 && <Tab className="tab fs-l">{t.dnf}</Tab>}
        </TabList>

        <TabPanel>
          {booksReading ? (
            <CurrentlyReading
              user={user}
              userData={userData}
              books={books}
              fetchBooks={fetchBooks}
              fetchUserData={fetchUserData}
              showNotification={showNotification}
              updateCurrentlyReading={updateCurrentlyReading}
            />
          ) : (
            <EmptyState
              state={'reading'}
              booksReading={booksReading}
              booksWantToRead={booksWantToRead}
              booksRead={booksRead}
            />
          )}
        </TabPanel>
        <TabPanel>
          {booksWantToRead > 0 && (
            <FilterInput
              searchText={searchText}
              setSearchText={setSearchText}
              t={t}
            />
          )}
          {booksWantToRead > 0 && filteredBooksWantToRead.length === 0 && (
            <div className="flex align-center pt-65">
              {t.nobooksmatchingfilter}
            </div>
          )}
          <div className="card-wrapper">
            {booksWantToRead > 0 ? (
              filteredBooksWantToRead.map((book) => (
                <div key={book.id} className="card book-card">
                  <div className="card-medium">
                    <img
                      src={updateProtocol(book.cover_url[0]) || bookCover}
                      alt={book.title}
                      height={120}
                      width={80}
                      className="card-medium-image"
                      loading="lazy"
                    />
                    <BookInfo book={book} t={t} />
                  </div>
                  <div className="separator"></div>
                  <div className="btn-group-right">
                    <div className="date fs-s pt-65">
                      <img
                        className="icon"
                        src={calendarLine}
                        width={20}
                        height={20}
                        alt="Calendar icon"
                      />
                      {moment(book.createdAt.toDate())
                        .locale(lang)
                        .format('D MMM, YYYY')}
                    </div>
                    <BookDetails
                      user={user}
                      book={book}
                      buttonClass={'btn btn-sm btn-primary'}
                      fetchBooks={fetchBooks}
                      fetchUserData={fetchUserData}
                      showNotification={showNotification}
                      updateCurrentlyReading={updateCurrentlyReading}
                    />
                    {book.status !== 'reading' && (
                      <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => markBookAsRead(book, false, true)}
                        disabled={moving}
                      >
                        {t.movetoreading}
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <EmptyState
                state={'wantToRead'}
                booksReading={booksReading}
                booksWantToRead={booksWantToRead}
                booksRead={booksRead}
              />
            )}
          </div>
        </TabPanel>
        <TabPanel>
          {booksRead > 0 && (
            <FilterInput
              searchText={searchText}
              setSearchText={setSearchText}
              t={t}
            />
          )}
          {booksRead > 0 && filteredBooksRead.length === 0 && (
            <div className="flex align-center pt-65">
              {t.nobooksmatchingfilter}
            </div>
          )}
          <div className="card-wrapper-double">
            {booksRead > 0 ? (
              filteredBooksRead
                .sort((a, b) => b.dateFinished - a.dateFinished)
                .map((book) => (
                  <div key={book.id} className="card book-card">
                    <div className="card-small">
                      <img
                        src={updateProtocol(book.cover_url[0]) || bookCover}
                        alt={book.title}
                        className="card-small-image"
                        loading="lazy"
                      />
                      <div className="book-info">
                        <div className="book-header">
                          <h2 className="fs-l">{book.title}</h2>
                          {book.author_name && (
                            <span className="fs-m pt-85">
                              {book.author_name ? book.author_name : 'N/A'}
                            </span>
                          )}
                        </div>
                        <StaticStarRating rating={book.rating} />
                      </div>
                    </div>
                    <div className="btn-group-right flexwrap">
                      {book.dateFinished && (
                        <div className="date fs-s pt-65 nowrap">
                          {moment(book.dateFinished.toDate())
                            .locale(lang)
                            .format('D MMM, YYYY')}
                        </div>
                      )}
                      <BookDetails
                        user={user}
                        book={book}
                        buttonClass={'btn btn-sm btn-primary'}
                        fetchBooks={fetchBooks}
                        fetchUserData={fetchUserData}
                        showNotification={showNotification}
                        updateCurrentlyReading={updateCurrentlyReading}
                      />
                    </div>
                  </div>
                ))
            ) : (
              <EmptyState
                state={'read'}
                booksReading={booksReading}
                booksWantToRead={booksWantToRead}
                booksRead={booksRead}
              />
            )}
          </div>
        </TabPanel>
        {booksDNF > 0 && (
          <TabPanel>
            <FilterInput
              searchText={searchText}
              setSearchText={setSearchText}
              t={t}
            />
            {filteredBooksDNF.length === 0 && (
              <div className="flex align-center pt-65">
                {t.nobooksmatchingfilter}
              </div>
            )}
            <div className="card-wrapper-double">
              {booksDNF > 0 &&
                filteredBooksDNF.map((book) => (
                  <div key={book.id} className="card book-card">
                    <div className="card-small">
                      <img
                        src={updateProtocol(book.cover_url[0]) || bookCover}
                        alt={book.title}
                        className="card-small-image"
                        loading="lazy"
                      />
                      <div className="book-info">
                        <div className="book-header">
                          <h2 className="fs-l">{book.title}</h2>
                          {book.author_name && (
                            <span className="fs-m pt-85">
                              {book.author_name ? book.author_name : 'N/A'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="btn-group-right flexwrap">
                      {book.dateFinished && (
                        <div className="date fs-s pt-65 nowrap">
                          {moment(book.dateFinished.toDate())
                            .locale(lang)
                            .format('D MMM, YYYY')}
                        </div>
                      )}
                      <BookDetails
                        user={user}
                        book={book}
                        buttonClass={'btn btn-sm btn-primary'}
                        fetchBooks={fetchBooks}
                        fetchUserData={fetchUserData}
                        showNotification={showNotification}
                        updateCurrentlyReading={updateCurrentlyReading}
                      />
                    </div>
                  </div>
                ))}
            </div>
          </TabPanel>
        )}
      </Tabs>
    </div>
  );
};

export default UserBooks;
